import { css } from '@emotion/react';
import { getInputHeightBySize, getInputSpacingBySize, input_wrapper_base_css } from '../input/styles';
import { semantic_colors, colors, text_styles, shapes, Typography } from '../../foundation';
import { InputSize } from '../input/types';

export const dropdown_label_base_css = css`
  ${input_wrapper_base_css};
  justify-content: space-between;
  color: ${semantic_colors.content.tertiary};
  border: 1px solid ${semantic_colors.border.secondary};
  background-color: ${semantic_colors.background.surface};
  &:focus {
    outline: 3px solid ${semantic_colors.tab_focus};
    box-shadow: none;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const dropdown_label_selected_style = {
  color: semantic_colors.content.primary,
};

export const dropdown_filter_label_selected_style = {
  'color': semantic_colors.accent.primary,
  'backgroundColor': semantic_colors.accent.light,
  'border': `1px solid ${semantic_colors.accent.primary}`,
  '&:hover': {
    border: `1px solid ${semantic_colors.accent.primary}`,
  },
};

export const dropdown_filter_label_error_style = {
  color: semantic_colors.state.negative,
  backgroundColor: semantic_colors.state.negative_light,
  border: `1px solid ${semantic_colors.state.negative_light}`,
};

// DropdownFilter's 32px trigger keeps icon, label, and arrow spacing tighter than the base Input gap.
export const getDropdownFilterLabelSpacingBySize = (size: InputSize) => {
  if (size === 'xsmall') return 4;
  if (size === 'small') return 6;
  return 8;
};

export const getDropdownFilterLabelSizeStyle = (size: InputSize) => {
  if (size !== 'small') return undefined;

  return css`
    column-gap: ${getDropdownFilterLabelSpacingBySize(size)}px;
    padding: 0 10px;
  `;
};

export const dropdown_item_group_css = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 8px;
  background-color: ${semantic_colors.background.surface};
  ${shapes.shadow.low};
  ${shapes.border_radius.small};
  &:focus {
    outline: none;
  }
`;

export const dropdown_filter_item_group_css = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  gap: 12px;
  &:focus {
    outline: none;
  }
`;

export const dropdown_item_base_css = css`
  background-color: ${semantic_colors.background.surface};
  color: ${semantic_colors.content.primary};
  display: flex;
  align-items: center;
  list-style: none;
  width: 100%;
  cursor: pointer;
  ${shapes.border_radius.small};
  transition: all 0.1s linear;

  &:hover {
    background-color: ${colors.gray10};
  }
  &:focus {
    outline: none;
    background-color: ${colors.gray10};
  }
  &:active {
    background-color: ${colors.gray20};
  }
`;

export const dropdown_filter_item_base_css = css`
  display: flex;
  align-items: center;
  list-style: none;
  width: 100%;
  &:focus {
    outline: none;
  }
`;

export const dropdown_item_disabled_style = {
  'color': semantic_colors.content.disabled,
  'cursor': 'not-allowed',
  '&:hover, &:focus': {
    backgroundColor: semantic_colors.background.surface,
  },
};

export const dropdown_item_selected_style = {
  color: semantic_colors.accent.primary,
  backgroundColor: colors.gray10,
};

export const dropdown_item_focused_style = {
  backgroundColor: colors.gray10,
};

export const dropdown_item_group_focus_style = {
  'outline': `3px solid ${semantic_colors.tab_focus}`,
  'zIndex': 1,
  '&:focus': {
    outline: `3px solid ${semantic_colors.tab_focus}`,
  },
};

export const dropdown_filter_item_style = {
  'backgroundColor': semantic_colors.background.surface,
  'borderRadius': 0,
  'height': 'auto',
  'padding': '12px 4px',
  '&:hover, &:focus, &:active': {
    backgroundColor: semantic_colors.background.surface,
  },
};

export const getDropdownChipLabelPaddingBySize = (size: InputSize) => {
  const padding: { [key in InputSize]: string } = {
    xsmall: '2px 8px',
    small: '4px 10px',
    medium: '5px 12px',
    large: '7px 16px',
  };
  return padding[size];
};

export const getDropdownItemFontStyle = (size: InputSize) => {
  const fonts: Record<InputSize, Typography> = {
    xsmall: 'Body_13_Regular',
    small: 'Body_13_Regular',
    medium: 'Body_13_Regular',
    large: 'Body_14_Regular',
  };
  return text_styles[fonts[size]];
};

export const getDropdownItemSizeStyle = (size: InputSize) => {
  const horizontalPadding: Record<InputSize, number> = { xsmall: 8, small: 10, medium: 12, large: 16 };

  return css`
    height: ${getInputHeightBySize(size)}px;
    column-gap: ${getInputSpacingBySize(size)}px;
    padding: 0 ${horizontalPadding[size]}px;
  `;
};
